import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';

const csvHeaders = [
  { label: 'Stock ID', key: 'StockId' },
  { label: 'Item ID', key: 'ItemId' },
  { label: 'Item Name', key: 'ItemName' },
  { label: 'Last Stock Updated', key: 'Last Stock Updated' },
  { label: 'Available Stock', key: 'available Stock' },
];

const StockReport = () => {
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchStockData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('https://serverrion.mylksystems.store/sales/getStock');
        const data = response.data || [];
        data.sort((a, b) => {
          const nameA = (a.ItemName || '').toLowerCase();
          const nameB = (b.ItemName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setStockData(data);
      } catch (err) {
        console.error('Failed to fetch stock report', err);
        setError('Unable to load stock data at the moment.');
      } finally {
        setLoading(false);
      }
    };

    fetchStockData();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const filteredData = useMemo(() => {
    if (!stockData.length) return [];
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) {
      return stockData;
    }
    return stockData.filter((stock) =>
      stock.ItemName?.toLowerCase().includes(normalized) ||
      String(stock.ItemId || '').toLowerCase().includes(normalized)
    );
  }, [stockData, searchTerm]);

  const paginatedData = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredData;
    }
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const totalStockQuantity = useMemo(
    () =>
      filteredData.reduce(
        (accumulator, item) => accumulator + Number(item['available Stock'] || 0),
        0
      ),
    [filteredData]
  );

  const lowStockCount = useMemo(
    () =>
      filteredData.filter((item) => Number(item['available Stock'] || 0) <= 5).length,
    [filteredData]
  );

  const csvData = useMemo(() => filteredData, [filteredData]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Inventory Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review current stock levels with fast search and CSV export.
          </Typography>
        </Box>
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename="inventory-report.csv"
          style={{ textDecoration: 'none' }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            disabled={!filteredData.length}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Total Items (Filtered)
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {filteredData.length.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Total Available Stock
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {totalStockQuantity.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Low Stock Items (≤ 5)
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {lowStockCount.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          label="Search by Item Name or ID"
          placeholder="Start typing to filter"
          fullWidth
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#000000' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Stock ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Item ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Item Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Last Stock Updated</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Available Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No inventory items match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => (
                  <TableRow key={row.StockId}>
                    <TableCell>{row.StockId}</TableCell>
                    <TableCell>{row.ItemId}</TableCell>
                    <TableCell>{row.ItemName}</TableCell>
                    <TableCell>{row['Last Stock Updated']}</TableCell>
                    <TableCell>{row['available Stock']}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
          sx={{ mt: 2 }}
        />
      )}
    </Box>
  );
};

export default StockReport;
